import type { Schema } from 'effect';

import {
  makeCreateAddress,
  type CreateAddressRequestBody,
} from '../../request/backendApi/address/makeCreateAddress';
import {
  makeUpdateAddress,
  type UpdateAddressRequestBody,
} from '../../request/backendApi/address/makeUpdateAddress';
import { makeGetAlertMessages } from '../../request/backendApi/alertMessage/makeGetAlertMessages';
import { makeGetFavouriteListProducts } from '../../request/backendApi/favourites/makeGetFavouriteListProducts';

export type MiscModuleDeps<
  AddressShowSchema extends Schema.Top,
  AlertMessageIndexSchema extends Schema.Top,
  ProductBaseSchema extends Schema.Top,
  AddressCreateInput,
  AddressUpdateInput,
> = {
  readonly addressShow: AddressShowSchema;
  readonly alertMessageIndex: AlertMessageIndexSchema;
  readonly productBase: ProductBaseSchema;
  readonly addressCreateBody: (data: AddressCreateInput) => CreateAddressRequestBody;
  readonly addressFirstName: (data: AddressCreateInput) => string;
  readonly addressTraceFields: (data: AddressCreateInput) => {
    readonly firstName: string;
    readonly lastName: string;
  };
  readonly addressUpdateBody: (data: AddressUpdateInput) => UpdateAddressRequestBody;
};

export const makeMiscModule = <
  AddressShowSchema extends Schema.Top,
  AlertMessageIndexSchema extends Schema.Top,
  ProductBaseSchema extends Schema.Top,
  AddressCreateInput,
  AddressUpdateInput,
>(
  deps: MiscModuleDeps<
    AddressShowSchema,
    AlertMessageIndexSchema,
    ProductBaseSchema,
    AddressCreateInput,
    AddressUpdateInput
  >
) => {
  const { CreateAddressError, createAddress } = makeCreateAddress({
    addressShow: deps.addressShow,
    addressCreateBody: deps.addressCreateBody,
    addressFirstName: deps.addressFirstName,
    addressTraceFields: deps.addressTraceFields,
  });
  const { UpdateAddressError, updateAddress } = makeUpdateAddress({
    addressShow: deps.addressShow,
    addressUpdateBody: deps.addressUpdateBody,
  });
  const { ListAlertMessagesError, listAlertMessages } = makeGetAlertMessages({
    alertMessageIndex: deps.alertMessageIndex,
  });
  const { GetFavouriteListProductsError, requestFavouriteListProducts } =
    makeGetFavouriteListProducts({
      productBase: deps.productBase,
    });

  return {
    CreateAddressError,
    UpdateAddressError,
    ListAlertMessagesError,
    GetFavouriteListProductsError,
    createAddress,
    updateAddress,
    listAlertMessages,
    requestFavouriteListProducts,
  };
};
